// LanguageBanner.jsx
//
// Pass-9 first-visit affordance. The pass-7 fullscreen modal that auto-
// opened on first visit was overwhelming as a welcome — it sat in front
// of the page and demanded a choice before the user could read anything.
//
// Replaced with a thin strip above the navbar offering the three most
// common locales inline (English · Español · 中文), plus a "More" link
// that opens the full LanguagePicker modal (which keeps all 38 locales
// for users who need them). A small X dismisses without setting a
// preference; the navbar globe icon is the always-on way back to the
// picker.
//
// Persistence: the banner hides as soon as the user picks any locale OR
// dismisses. Both write to LOCALE_STORAGE_KEY ('518cares-locale-v1') —
// dismissing is treated as "the user has acknowledged the choice, keep
// it in English." Aligns with the existing convention so we don't
// introduce a second dismissal flag.

import React, { useCallback } from 'react';
import { FiX, FiGlobe } from 'react-icons/fi';
import { useLocaleSelector } from '../lib/gt-shim';
import { LOCALE_STORAGE_KEY } from './LanguagePicker';
import '../styles/LanguageBanner.css';

// The inline featured locales — the three most common in the Capital
// District. The "More" link opens the full picker for everyone else.
const INLINE_LOCALES = [
  { code: 'en', nativeName: 'English' },
  { code: 'es', nativeName: 'Español' },
  { code: 'zh', nativeName: '中文' },
];

const LanguageBanner = ({ open, onDismiss, onOpenPicker }) => {
  const { setLocale } = useLocaleSelector();

  const pick = useCallback(
    (code) => {
      try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
      } catch {
        /* private mode / storage disabled */
      }
      setLocale(code);
      onDismiss();
    },
    [setLocale, onDismiss]
  );

  const dismiss = useCallback(() => {
    // Treat explicit X as "stay in English" — write the locale so the
    // banner doesn't show on the next visit and the navbar globe badge
    // reflects the current state.
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, 'en');
    } catch {
      /* noop */
    }
    onDismiss();
  }, [onDismiss]);

  if (!open) return null;

  return (
    <div className="language-banner" role="region" aria-label="Choose language">
      <div className="language-banner__inner">
        <span className="language-banner__icon" aria-hidden="true">
          <FiGlobe />
        </span>
        <span className="language-banner__prompt">Read this in</span>
        <span className="language-banner__sep" aria-hidden="true">·</span>
        {INLINE_LOCALES.map((loc, i) => (
          <React.Fragment key={loc.code}>
            <button
              type="button"
              className="language-banner__option"
              onClick={() => pick(loc.code)}
              lang={loc.code}>
              {loc.nativeName}
            </button>
            {i < INLINE_LOCALES.length - 1 && (
              <span className="language-banner__sep" aria-hidden="true">·</span>
            )}
          </React.Fragment>
        ))}
        <span className="language-banner__sep" aria-hidden="true">·</span>
        <button
          type="button"
          className="language-banner__more"
          onClick={onOpenPicker}>
          More
        </button>
      </div>
      <button
        type="button"
        className="language-banner__dismiss"
        onClick={dismiss}
        aria-label="Dismiss language banner">
        <FiX />
      </button>
    </div>
  );
};

export default LanguageBanner;
